import { api } from "../../lib";

export interface Facility {
  id: string;
  name: string;
  address: string;
  phone?: string | null;
  fax?: string | null;
  logo_s3_key?: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // camelCase aliases for compatibility
  isActive?: boolean;
  logoS3Key?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateFacilityInput {
  name: string;
  address: string;
  phone?: string;
  fax?: string;
  is_active?: boolean;
}

export interface UpdateFacilityInput extends Partial<CreateFacilityInput> {
  id: string;
  logoS3Key?: string;
}

export interface FacilityLogoPresignedUrlResponse {
  url: string;
  key: string;
}

// Normalize facility to have both snake_case and camelCase properties
function normalizeFacility(facility: any): Facility {
  return {
    ...facility,
    is_active: facility.is_active ?? facility.isActive,
    created_at: facility.created_at || facility.createdAt,
    updated_at: facility.updated_at || facility.updatedAt,
    phone: facility.phone ?? null,
    fax: facility.fax ?? null,
    logo_s3_key: facility.logo_s3_key || facility.logoS3Key || null,
    isActive: facility.isActive ?? facility.is_active,
    logoS3Key: facility.logoS3Key || facility.logo_s3_key || null,
    createdAt: facility.createdAt || facility.created_at,
    updatedAt: facility.updatedAt || facility.updated_at,
  };
}

export const facilitiesService = {
  getAll: async () => {
    const facilities = await api.get<Facility[]>("/facilities");
    return facilities.map(normalizeFacility);
  },

  getActive: async () => {
    const facilities = await api.get<Facility[]>("/facilities/active");
    return facilities.map(normalizeFacility);
  },

  getById: async (id: string) => {
    const facility = await api.get<Facility>(`/facilities/${id}`);
    return normalizeFacility(facility);
  },

  create: async (input: CreateFacilityInput) => {
    const facility = await api.post<Facility>("/facilities", {
      name: input.name,
      address: input.address,
      is_active: input.is_active ?? true,
    });
    return normalizeFacility(facility);
  },

  update: async (input: UpdateFacilityInput) => {
    const { id, ...updates } = input;
    const facility = await api.put<Facility>(`/facilities/${id}`, updates);
    return normalizeFacility(facility);
  },

  toggleActive: async (id: string) => {
    const facility = await api.patch<Facility>(
      `/facilities/${id}/toggle-active`
    );
    return normalizeFacility(facility);
  },

  delete: async (id: string) => {
    await api.delete<void>(`/facilities/${id}`);
  },

  getLogoPresignedUrl: async (
    fileName: string,
    contentType: string
  ): Promise<FacilityLogoPresignedUrlResponse> => {
    return api.post<FacilityLogoPresignedUrlResponse>(
      "/documents/presigned-url/facility-logos",
      { fileName, contentType }
    );
  },

  uploadLogoToS3: async (url: string, file: File): Promise<void> => {
    await fetch(url, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
  },

  updateLogoS3Key: async (id: string, logoS3Key: string): Promise<Facility> => {
    const facility = await api.patch<Facility>(`/facilities/${id}/logo`, {
      logoS3Key,
    });
    return normalizeFacility(facility);
  },

  getLogoDownloadUrl: async (key: string): Promise<string> => {
    const { url } = await api.post<{ url: string }>("/documents/download-url", {
      key,
    });
    return url;
  },
};
